import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import { localBusinessSchema, buildBreadcrumbSchema, buildFaqSchema, buildServiceSchema, buildGraphSchema } from '@/components/base/schemas';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const serviceSchema = buildServiceSchema(
  'Réparation de fenêtres bois à Paris 15ᵉ',
  'Réparation fenêtres bois dans le 15ᵉ : quincaillerie, crémone, réglage, étanchéité.'
);

const breadcrumbSchema = buildBreadcrumbSchema([
  { name: 'Accueil', item: 'https://fenetre-paris15.fr/' },
  { name: 'Fenêtres Bois', item: 'https://fenetre-paris15.fr/reparation-fenetre-bois-paris-15/' },
]);

const faqs = [
  { q: `Peut-on réparer une crémone de fenêtre bois ancienne ?`, a: `Oui, c'est une de nos spécialités. Les crémones d'époque, souvent en laiton ou acier, peuvent être réparées ou remplacées par des modèles adaptés au style et aux dimensions de la menuiserie d'origine. Nous travaillons avec des fournisseurs spécialisés en quincaillerie de rénovation.` },
  { q: `Ma fenêtre bois gonfle en hiver et coince. Que faire ?`, a: `Le bois est un matériau vivant qui réagit à l'humidité. Un vantail qui coince en hiver est souvent dû à un gonflement. Nous pouvons ajuster les paumelles, raboter légèrement le chant concerné, ou améliorer l'étanchéité pour limiter la prise d'humidité.` },
  { q: `Intervenez-vous sur les fenêtres bois classées ou en copropriété ?`, a: `Absolument. Nous connaissons les contraintes des bâtiments haussmanniens et des copropriétés classées. Nos interventions sur la quincaillerie ne modifient pas l'aspect extérieur. Nous travaillons dans le respect des règles de copropriété et pouvons fournir un devis détaillé pour le syndic.` },
];

const pannes = [
  { title: `Gonflement du bois`, desc: `Le bois travaille avec l'humidité. Un vantail qui coince en hiver peut être ajusté par réglage des paumelles ou léger rabotage du chant concerné. Nous ne traitons pas le bois lui-même.` },
  { title: `Quincaillerie ancienne bloquée`, desc: `Crémones, espagnolettes, targettes : ces mécanismes d'époque s'encrassent et se grippent. Notre technicien les nettoie, les lubrifie ou les remplace par un modèle adapté.` },
  { title: `Crémone d'époque à remplacer`, desc: `Quand la pièce est trop usée, nous trouvons un équivalent moderne compatible ou une reproduction. Nous travaillons avec des quincailliers spécialisés en rénovation.` },
  { title: `Étanchéité défaillante`, desc: `Les joints bois d'origine n'existaient souvent pas. Nous pouvons poser des joints discrets adaptés pour améliorer l'étanchéité sans modifier l'esthétique.` },
];

export default function BoisPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <Layout>
      <SEOHead
        title="Réparation Fenêtre Bois Paris 15 (75015) | DF PRO"
        description="Réparation fenêtres bois dans le 15ᵉ : quincaillerie, crémone, réglage, étanchéité. Devis gratuit, intervention rapide. ☎ 01 87 66 81 55"
        canonicalUrl="https://fenetre-paris15.fr/reparation-fenetre-bois-paris-15/"
        schema={buildGraphSchema(localBusinessSchema, breadcrumbSchema, buildFaqSchema(faqs), serviceSchema)}
      />

      <div className="bg-brand-mint/30 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="Fil d'Ariane" className="text-xs text-brand-text/60">
            <Link to="/" className="hover:text-brand-accent">Accueil</Link><span className="mx-2">/</span><span className="text-brand-accent font-medium">Fenêtres Bois</span>
          </nav>
        </div>
      </div>

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-brand-dark leading-tight mb-4">Réparation de fenêtres bois à Paris 15ᵉ</h1>
              <p className="text-brand-text/70 text-base leading-relaxed mb-6">
                Le bois est l'âme du bâti haussmannien qui fait le charme de Vaugirard, Saint-Lambert et Grenelle. DF PRO est spécialiste de la <strong>réparation de quincaillerie de fenêtres bois</strong> : nous restaurons le fonctionnement de vos menuiseries anciennes sans toucher au traitement ni à la peinture.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
                <a href="tel:+33187668155" className="btn-outline text-sm"><i className="ri-phone-line"></i> 01 87 66 81 55</a>
              </div>
            </div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img src="/images/dfpro-bois-hero.webp" alt="Fenêtre bois haussmannienne à Paris — DF PRO réparation fenêtre bois Paris 15" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-brand-mint/40">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-3">Le bois dans le 15ᵉ : un patrimoine à préserver</h2>
          <p className="text-brand-text/70 leading-relaxed mb-8 max-w-3xl">
            Le 15ᵉ arrondissement compte un nombre important d'immeubles haussmanniens et post-haussmanniens, notamment dans les quartiers Vaugirard, Saint-Lambert, Necker et Grenelle. Ces bâtiments sont équipés de fenêtres bois d'origine ou de première rénovation, avec des crémones à espagnolette, des mécanismes en laiton et des paumelles à visser caractéristiques. Ces menuiseries de qualité peuvent fonctionner plus d'un siècle si elles sont bien entretenues. Notre mission : préserver ce patrimoine en restaurant la quincaillerie sans dénaturer l'existant.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {pannes.map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5 md:p-6">
                <h3 className="font-heading font-bold text-brand-dark text-base mb-2">{item.title}</h3>
                <p className="text-brand-text/60 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            <div>
              <h2 className="section-title mb-4">Notre périmètre d'intervention</h2>
              <p className="text-brand-text/70 text-sm leading-relaxed mb-4">DF PRO intervient exclusivement sur la <strong>quincaillerie et les mécanismes</strong> des fenêtres bois. Ce que nous réparons :</p>
              <ul className="space-y-2 text-brand-text/70 text-sm">
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Crémones, espagnolettes, targettes et verrous</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Poignées d'époque (adaptation ou remplacement)</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Paumelles, fiches et charnières</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Compas et entrebâilleurs</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Réglage général de l'ouvrant et dégrippage</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-brand-accent mt-0.5"></i> Pose de joints d'étanchéité discrets</li>
              </ul>
              <p className="text-brand-text/50 text-xs italic mt-4">Hors périmètre : traitement du bois, peinture, remplacement de vitrage, création de fenêtres sur mesure.</p>
            </div>
            <div className="bg-gradient-to-br from-brand-gradientFrom to-brand-gradientTo rounded-xl p-6 md:p-8 text-white">
              <h2 className="font-heading font-bold text-xl mb-4">Conservation du patrimoine</h2>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                Remplacer une fenêtre bois haussmannienne par du PVC est souvent interdit par le règlement de copropriété — et c'est tant mieux pour le cachet de l'immeuble. DF PRO vous aide à <strong>conserver vos menuiseries d'origine</strong> en bon état de fonctionnement, pour une fraction du coût d'un remplacement.
              </p>
              <p className="text-white/80 text-sm leading-relaxed mb-6">
                Pour les copropriétés et syndics, nous fournissons des devis détaillés, adaptés aux exigences des assemblées générales.
              </p>
              <Link to="/devis-fenetre-paris-15/" className="inline-flex items-center gap-2 bg-white text-brand-dark font-semibold rounded-full px-6 py-3 text-sm hover:opacity-90 transition-opacity whitespace-nowrap">
                <i className="ri-file-text-line"></i> Devis gratuit
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-brand-mint/40">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-8 text-center">Questions fréquentes — Fenêtres Bois</h2>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-brand-mint/30 transition-colors">
                  <span className="font-heading font-bold text-brand-dark text-sm pr-4">{faq.q}</span>
                  <i className={`ri-${openFaq === idx ? 'subtract' : 'add'}-line text-brand-accent text-lg flex-shrink-0`}></i>
                </button>
                {openFaq === idx && <div className="px-5 pb-4"><p className="text-brand-text/70 text-sm">{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white text-center">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="section-title mb-4">Votre fenêtre bois mérite un expert</h2>
          <p className="section-subtitle mx-auto mb-6">Confiez la quincaillerie de vos menuiseries anciennes à DF PRO</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
            <Link to="/devis-fenetre-paris-15/" className="btn-primary text-sm">Devis gratuit</Link>
            <a href="tel:+33187668155" className="btn-outline text-sm"><i className="ri-phone-line"></i> 01 87 66 81 55</a>
          </div>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/zones-intervention-paris-15/" className="text-brand-accent hover:underline">Zones d'intervention</Link>
            <span className="text-brand-text/30">|</span>
            <Link to="/reparation-fenetre-pvc-paris-15/" className="text-brand-accent hover:underline">Fenêtres PVC</Link>
            <span className="text-brand-text/30">|</span>
            <Link to="/blog/double-vitrage-embue-que-faire/" className="text-brand-accent hover:underline">Article : double vitrage embué</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}